import React, { useRef, useState, useEffect } from "react";
import "./css/Feature3.css";

export default function Feature3() {
  // --- Tilt / pointer tracking (same feel as Feature2) ---
  const cardRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const rafRef = useRef(null);

  // replace with your final IPFS image if different
  const CARD_IMAGE =
    "https://pcgslabs.infura-ipfs.io/ipfs/QmaGsYcR8NQX6j4mufWSnv4J2PDrFy2KWxe2QkC6PJSCGp";

  const handlePointerMove = (e) => {
    const el = cardRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const x = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const y = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (typeof x !== "number" || typeof y !== "number") return;

    const px = (x - rect.left) / rect.width; // 0..1
    const py = (y - rect.top) / rect.height; // 0..1

    const maxTilt = 12;
    const ry = (px - 0.5) * (maxTilt * 2);
    const rx = (0.5 - py) * (maxTilt * 2);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      el.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
      el.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
      el.style.setProperty("--px", `${(px * 100).toFixed(2)}%`);
      el.style.setProperty("--py", `${(py * 100).toFixed(2)}%`);
    });
  };

  const resetTilt = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--px", "50%");
    el.style.setProperty("--py", "50%");
  };

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // --- Link click persistence (green after click) ---
  const handleLinkClick = (e) => {
    e.currentTarget.classList.add("clicked");
  };

  // --- Feature1 heart / particle burst (bottom-right) ---
  const heartRef = useRef(null);
  const FADE_DURATION = 1000; // ms

  const onHeartActivate = () => {
    const btn = heartRef.current;
    if (!btn) return;

    btn.classList.toggle("liked");
    const isLiked = btn.classList.contains("liked");
    if (!isLiked) return;

    const particles = [];
    for (let i = 0; i < 5; i += 1) {
      const particle = document.createElement("span");
      particle.classList.add("particle");
      const angle = random(0, 360);
      const distance = random(28, 38);
      particle.style.setProperty("--angle", angle + "deg");
      particle.style.setProperty("--distance", distance + "px");
      particle.style.setProperty("--fade-duration", FADE_DURATION + "ms");
      btn.appendChild(particle);
      particles.push(particle);
    }
    window.setTimeout(
      () => particles.forEach((p) => p.remove()),
      FADE_DURATION + 200
    );
  };

  const onHeartKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onHeartActivate();
    }
  };

  return (
    <section className="feature3">
      <h2>Feature 3</h2>
      <p className="subtitle">Combined tilt card + heart particle burst.</p>

      <div className="card-stage">
        {/* role=button avoids nesting a <button> inside a <button> */}
        <div
          className={`tilt-card ${hovering ? "is-hover" : ""}`}
          ref={cardRef}
          role="button"
          tabIndex={0}
          aria-label="Interactive trading card"
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => {
            setHovering(false);
            resetTilt();
          }}
          onMouseMove={handlePointerMove}
          onTouchStart={(e) => {
            setHovering(true);
            handlePointerMove(e);
          }}
          onTouchMove={handlePointerMove}
          onTouchEnd={() => {
            setHovering(false);
            resetTilt();
          }}
          onBlur={() => {
            setHovering(false);
            resetTilt();
          }}
        >
          {/* Full-bleed image (no overlays) */}
          <img className="card-img" src={CARD_IMAGE} alt="Card artwork" />

          {/* Frame outline */}
          <span className="frame" aria-hidden="true" />

          {/* Title at top-left */}
          <span className="label title-label" aria-hidden="true">
            PCGSLABS // ID: ####
          </span>

          {/* Clickable link labels at bottom-left */}
          <div className="labels-group">
            <a
              className="label"
              href="https://www.google.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
            >
              NFT
            </a>
            <a
              className="label"
              href="https://www.google.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
            >
              History
            </a>
            <a
              className="label"
              href="https://www.google.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
            >
              Exchange
            </a>
            <a
              className="label"
              href="https://www.google.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
            >
              Pop Report
            </a>
          </div>

          {/* Heart button at bottom-right */}
          <button
            type="button"
            className="particleButton"
            ref={heartRef}
            onClick={onHeartActivate}
            onKeyDown={onHeartKeyDown}
            aria-pressed="false"
            aria-label="Like this card"
            title="Like"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M3.68546 5.43796C8.61936 1.29159 11.8685 7.4309 12.0406 7.4309C12.2126 7.43091 15.4617 1.29159 20.3956 5.43796C26.8941 10.8991 13.5 21.8215 12.0406 21.8215C10.5811 21.8215 -2.81297 10.8991 3.68546 5.43796Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="visually-hidden">Like this card</span>
          </button>
        </div>
      </div>
    </section>
  );
}

/* helpers */
function random(lower = 0, upper = 1, floating) {
  if (upper === undefined) {
    upper = lower;
    lower = 0;
  }
  if (
    floating === true ||
    !Number.isInteger(lower) ||
    !Number.isInteger(upper)
  ) {
    return Math.random() * (upper - lower) + lower;
  }
  return Math.floor(Math.random() * (upper - lower + 1)) + lower;
}
